import { Style } from 'inlines'
import { useClient, useQuery } from '@based/sdk/react'
import { Text } from '@app/components/core/typography/Text'
import { ButtonProps } from '@app/components/core/button/Button'
import { faPlus, faCheck } from '@fortawesome/pro-regular-svg-icons'
import { Card } from './Card'
import { AvatarStack, ProfileAvatar } from '../avatarStack/AvatarStack'
import Column from '@app/components/layout/Column'
import { useState } from 'react'
import Row from '@app/components/layout/Row'
import { useUserContext } from '@app/util/hooks/useUserContext'
import { useTranslations } from '@i18n/useLanguage'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { SocialShareModal } from '../modal/SocialShareModal'
import { PageHeader, ProfileProps } from '../pageHeader/PageHeader'
import { enrich } from '@i18n/localizeHelper'

export type TopicCommunityHeaderCardManagedBy = {
  title: string
  slug: string
  avatar?: { src: string }
}

export type TopicCommunityHeaderCardProps = {
  topicCommunityId: number
  title: string
  bannerImageSrc?: string
  followers?: ProfileAvatar[]
  managedBy?: TopicCommunityHeaderCardManagedBy
  style?: Style
}

export const TopicCommunityHeaderCard = ({
  topicCommunityId,
  title,
  bannerImageSrc,
  followers = [],
  managedBy,
  style,
}: TopicCommunityHeaderCardProps) => {
  const client = useClient()
  const { user } = useUserContext()
  const t = useTranslations().app
  const { withAuthCheck } = useAuthModal()

  const followerCountQuery = useQuery('count-followers', {
    entityType: 'topicCommunity',
    entityIds: [topicCommunityId],
  })

  const [shareModalOpen, setShareModalOpen] = useState(false)
  const isFollowingQuery = useQuery(user?.id ? 'is-following' : null, {
    entityType: 'topicCommunity',
    entityId: topicCommunityId,
    userId: user?.id,
  })
  const isFollowing: boolean = isFollowingQuery.data ?? false

  const toggleFollow = () => {
    void withAuthCheck(async () => {
      await client.call('follow-topic-community', {
        topicCommunityId,
      })
    })
  }

  const buttons: ButtonProps[] = [
    {
      variant: 'secondary',
      label: t.common.share,
      onClick: () => setShareModalOpen(true),
    },
    {
      variant: 'primary',
      label: isFollowing
        ? t.topicCommunityPage.following
        : t.topicCommunityPage.follow,
      icon: isFollowing ? faCheck : faPlus,
      iconPosition: 'left',
      onClick: toggleFollow,
    },
  ]

  const profile: ProfileProps = managedBy
    ? {
        title: managedBy.title,
        slug: managedBy.slug,
        avatar: managedBy.avatar,
      }
    : undefined

  const followerAmount = followerCountQuery.data?.[0] ?? 0

  return (
    <Card
      image={bannerImageSrc}
      imageStyle={{
        height: '18rem',
      }}
      padded={false}
      border={true}
      style={{
        width: '100%',
        ...style,
      }}
      contentStyle={{
        padding: '1.5rem',
      }}
    >
      <Column
        gap="0"
        style={{
          flex: '1 0 0',
          width: '100%',
        }}
      >
        <PageHeader
          title={title}
          subtitle={managedBy ? t.topicCommunityPage.managedBy : undefined}
          profile={profile}
          buttons={buttons}
        />
        {followers.length > 0 && (
          <Row
            gap="0.5rem"
            style={{
              alignItems: 'center',
            }}
          >
            <AvatarStack
              size="extra-small"
              avatars={followers}
              version="dark"
            />
            <Text.Small
              style={{
                color: 'var(--shades-blue-500)',
              }}
            >
              {enrich(t.common.followersAmount, followerAmount)}
            </Text.Small>
          </Row>
        )}
      </Column>

      <SocialShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={title}
      />
    </Card>
  )
}
